import { useCallback, useEffect, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import {
  getCoverage,
  getResearch,
  getResearchProgress,
  isError,
  startSubjectResearch,
  type CoverageView,
  type ResearchDocument,
  type ResearchProgress,
  type ResearchSubject,
} from './api';
/*
 * What may be said about coverage is decided in a module a Node test can import, not here
 * (F-129). This screen renders sentences about whether a subject reached the plan, and the two
 * ways of getting that wrong are both silent — see `plan/coverage.ts`.
 */
import { reconcile, verdictsById } from './plan/coverage';
/*
 * The live panel, shared with the plan screen (F-147). It is handed the progress this screen has
 * already polled rather than fetching its own — two pollers for one number on one screen is how
 * two panels come to disagree about it.
 */
import ResearchPulse from './ResearchPulse';
/* The uploader is several panels down this same screen, and it owns everything about uploading. */
import { goToUpload } from './panels';

/**
 * Research, while it is happening and after it has.
 *
 * **The panel exists because "reading up on it" for fifteen minutes is not a progress
 * indicator, it is a promise.** Research used to be one call that either produced a
 * twenty-thousand-word document or produced nothing, and until it landed there was one
 * sentence on the screen that said the same thing whether the pass was thirty seconds from
 * finishing or had died an hour ago.
 *
 * It is now an outline followed by waves of batched per-subject research (F-109), and this
 * shows that: the subjects appear within a minute or two, each with a sentence, and then fill
 * in one wave at a time. Everything on screen is read from a row a pass actually wrote — the
 * overall fraction is computed by the API, and each subject's state is its own row. Nothing
 * here advances on a timer, because a bar that moves for no reason teaches somebody that the
 * bar means nothing.
 */

/**
 * How often to ask, and why it is two numbers.
 *
 * Five seconds while the outline runs, because that is a minute or two of somebody's actual
 * attention and the whole point is watching subjects appear. Thirty once the waves start,
 * because a wave is a Message Batch and batches on this workspace were measured at six to 109
 * minutes — a page polling every five seconds through an hour and a half of that is two
 * thousand requests to watch one number not change.
 */
const FAST_POLL_MS = 5_000;
const SLOW_POLL_MS = 30_000;

/**
 * When to stop asking on our own.
 *
 * Two hours, measured on the clock rather than counted in polls. A count is wrong at one of
 * the two cadences by definition: 240 polls is two hours of waves and twenty minutes of
 * outline, so an outline that hung would give up eight times sooner than the thing this number
 * is supposed to describe.
 *
 * Past two hours a tab has been left open rather than watched, and the push notification is
 * what actually brings somebody back — so the page stops and offers a button, the same shape
 * every other long wait in this app uses.
 */
const MAX_WATCH_MS = 2 * 60 * 60 * 1_000;

/*
 * The price that used to be on this button is gone, and it is not coming back.
 *
 * Leo, 2026-09-15: *"the user should not know about budget, we will hide that in our business
 * model… they will pay a monthly flat fee so budget is our concern."* A learner reading "Go
 * deeper — $0.25" is being asked to do accounting on their own curriculum, per subject, which
 * is a decision they cannot make well and did not sign up to make at all.
 *
 * **What replaces it is scope, not silence.** A subject that will not be researched is still
 * said out loud on this screen, because a plan that is quietly incomplete is worse than one
 * that says which parts of it are not covered. What changed is the reason given: it is what
 * this plan reaches, not what it costs.
 */

/** Subject states, mapped onto the three pill styles that exist. */
const PILL: Record<ResearchSubject['state'], string> = {
  outlined: 'pill pill-pending',
  queued: 'pill pill-pending',
  researching: 'pill pill-pending',
  deep: 'pill',
  // Thin, barren and unfunded are not failures and must not be red. `thin` means there was
  // little to find, `unfunded` that this run did not reach the subject, and `barren` that the
  // research ran and the world had nothing — which is a fact about the subject and not about
  // us. Every one of them has a button, and `barren` additionally says what is missing.
  thin: 'pill pill-pending',
  barren: 'pill pill-pending',
  unfunded: 'pill pill-pending',
  failed: 'pill pill-failed',
};

/**
 * The subject states that keep their own word rather than taking the report's.
 *
 * Both map to `barren` in the coverage contract, and that mapping is right for deciding what to
 * *offer* — nothing was retrieved, so the two remedies are the same. It is wrong for the label:
 * `unfunded` means the run stopped before reaching this subject and `failed` means the request
 * errored, and both are facts about us. Rendering either as "nothing found" would tell a learner
 * the world is empty of a subject nobody ever managed to look for.
 */
const KEEPS_ITS_STATE: ReadonlySet<ResearchSubject['state']> = new Set(['unfunded', 'failed']);

/** Which subjects are worth offering a deeper pass on, and which are already having one. */
const DEEPENABLE: ReadonlySet<ResearchSubject['state']> = new Set([
  'deep',
  'thin',
  // A subject the search found nothing for is the *most* worth offering another pass on, with
  // different angles — the cheap second attempt this pipeline has never made. See
  // `deepenRequest` in the pipeline, which tells a second round it is one.
  'barren',
  'unfunded',
  'failed',
]);

export default function Research({ objectiveId }: { objectiveId: string }) {
  const { t, locale } = useI18n();
  const [documents, setDocuments] = useState<ResearchDocument[] | undefined>();
  const [progress, setProgress] = useState<ResearchProgress | undefined>();
  const [coverage, setCoverage] = useState<CoverageView | undefined>();
  const [open, setOpen] = useState(false);
  const [deepening, setDeepening] = useState<string | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [stopped, setStopped] = useState(false);
  /**
   * Bumped to send the watcher round again after it has stopped.
   *
   * The poll loop terminates when a run reaches `done`, and its effect depends only on the
   * objective — so pressing "go deeper", which starts a *new* run on the same objective, would
   * otherwise leave the panel frozen on a single reading with no timer anywhere. Same for the
   * "check now" button, which without this is a one-shot refresh dressed as a resume.
   */
  const [watch, setWatch] = useState(0);

  /**
   * Fetches, and deliberately does not write state.
   *
   * **The three outcomes are different and two of them used to look identical.** A failed
   * request is not "the run finished"; an objective nobody has researched is not "the request
   * failed". Collapsing them is how a single offline blip left a running run displayed for
   * ever with no timer and no way back — the caller could not tell that it should reschedule.
   *
   * It returns rather than setting, so the caller can drop the answer if the objective changed
   * while it was in flight. Setting state from in here is the cross-objective leak the sibling
   * effect's own comment exists to warn about.
   */
  const load = useCallback(
    async (): Promise<
      { ok: true; progress: ResearchProgress | undefined } | { ok: false }
    > => {
      const result = await getResearchProgress(objectiveId);
      if (isError(result)) return { ok: false };
      if (!('run' in result) || !result.run) return { ok: true, progress: undefined };
      return { ok: true, progress: result as ResearchProgress };
    },
    [objectiveId],
  );

  useEffect(() => {
    let stale = false;
    void getResearch(objectiveId).then((result) => {
      // Guarded because switching objectives remounts this with a new id while the previous
      // request is still in flight — without it the old objective's research can land on the
      // new one's screen.
      if (stale) return;
      setDocuments(isError(result) ? [] : result.documents);
    });
    /*
     * The coverage report rides with the documents rather than with the poll, and that is the
     * whole of its cost control.
     *
     * It is three reads where the poll is one, and the answer cannot change between two polls
     * thirty seconds apart — coverage moves when a run settles or a plan is built, which is
     * exactly what this effect's dependency on the run state already watches. Folding it into
     * the five-second poll would triple the cost of the one screen guaranteed to be polled, for
     * an answer that had not moved.
     *
     * An unreadable report leaves the panel as it was rather than emptying it: every subject
     * still has its row, its state and its progress bar, and the only thing lost is the
     * sentence about the plan. Clearing it on a failed request would make one tunnel look like
     * a plan that suddenly covers nothing.
     */
    void getCoverage(objectiveId).then((result) => {
      if (stale || isError(result)) return;
      setCoverage(result);
    });
    return () => {
      stale = true;
    };
  }, [objectiveId, progress?.run.state]);

  /*
   * The poll, and it reschedules itself from the answer rather than running on a fixed timer.
   *
   * A `setInterval` at either speed would be wrong at the other, and switching intervals from
   * a dependency array means a render in the middle of a wave restarts the clock. A chained
   * timeout asks, reads what came back, and decides how long to wait next — which is also what
   * lets it stop cleanly when the run ends rather than firing once more into a finished run.
   */
  useEffect(() => {
    let stale = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const until = Date.now() + MAX_WATCH_MS;
    setStopped(false);

    const tick = async (): Promise<void> => {
      const result = await load();
      // Checked *after* the await and before anything is written: the objective may have
      // changed while this request was in flight, and landing the old one's progress on the new
      // one's screen is exactly the failure the sibling effect guards against.
      if (stale) return;

      if (!result.ok) {
        /*
         * The request failed, which says nothing about the run.
         *
         * Treating this as "finished" is how one 500 or one tunnel leaves a running run on
         * screen for ever with no timer and no button — the "check now" escape is gated on
         * having deliberately stopped, which this is not. So: keep the last good reading, and
         * come round again at the slow cadence.
         */
        timer = setTimeout(() => void tick(), SLOW_POLL_MS);
        return;
      }

      setProgress(result.progress);
      const state = result.progress?.run.state;
      if (state !== 'outlining' && state !== 'deepening') return;
      if (Date.now() >= until) {
        setStopped(true);
        return;
      }
      timer = setTimeout(() => void tick(), state === 'outlining' ? FAST_POLL_MS : SLOW_POLL_MS);
    };

    void tick();
    return () => {
      stale = true;
      if (timer) clearTimeout(timer);
    };
  }, [objectiveId, load, watch]);

  /** Reads once and puts the answer on screen, for the two controls that are not the loop. */
  const refresh = useCallback(async () => {
    const result = await load();
    if (result.ok) setProgress(result.progress);
  }, [load]);

  async function goDeeper(subject: ResearchSubject): Promise<void> {
    if (!window.confirm(t('res.deeper.confirm'))) return;
    setDeepening(subject.subjectId);
    setProblem(undefined);
    const result = await startSubjectResearch(objectiveId, subject.subjectId);
    if (isError(result)) {
      setDeepening(undefined);
      setProblem(explain(result.error));
      // Both refusals mean this screen is arguing with a row it has not read recently.
      // Reloading answers them; retrying against the same stale reading would not.
      void refresh();
      return;
    }
    setDeepening(undefined);
    // A new run on the same objective, so the watcher — which stopped when the last one
    // finished — has to be sent round again. Without this the panel freezes on one reading
    // while a batch the learner just paid for runs invisibly.
    setWatch((round) => round + 1);
  }

  function explain(error: string): string {
    if (error === 'already_running') return t('res.error.running');
    if (error === 'not_ready') return t('res.error.notReady');
    // Not an error: the plan builds itself once enough research has landed.
    if (error === 'still_researching') return t('plan.error.stillResearching');
    if (error === 'no_subject') return t('res.error.noSubject');
    return t('res.error.generic');
  }

  const run = progress?.run;
  const subjects = progress?.subjects ?? [];
  const working = run?.state === 'outlining' || run?.state === 'deepening';
  const deep = subjects.filter((subject) => subject.state === 'deep').length;
  /*
   * What happened to each subject, and the two numbers the learner is comparing.
   *
   * Hung off the progress rows by subject id rather than rendered as a list of its own: the
   * report and the progress panel are about the same twelve subjects, and a second list beside
   * the first is how "twelve subjects" and "five subjects" became two unexplained numbers in
   * the first place. A subject still being researched has no verdict, which is correct — it has
   * not been asked to reach the plan yet.
   */
  const verdicts = verdictsById(coverage);
  const summary = reconcile(coverage);
  /* Said only when there is a plan to compare against. Before one is built there is nothing to
     reconcile, and a line reading "0 in your plan" would report an ordinary state as a loss. */
  const comparable = coverage !== undefined && coverage.planNodes > 0 && summary.researched > 0;

  const when = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <>
      {run && (
        <section className="panel">
          <h2>{t('res.progress.title')}</h2>

          {/* A native `<progress>`, not a div pretending to be one. It carries the value to a
              screen reader without any ARIA of ours, and it cannot drift from the number
              beside it because there is only one number. */}
          <progress value={progress?.overall ?? 0} max={1} />
          <p className="muted small">
            {t(`res.progress.${run.state}` as CopyKey)}
            {subjects.length > 0
              ? ` · ${t('res.progress.of')
                  .replace('{done}', String(deep))
                  .replace('{total}', String(subjects.length))}`
              : ''}
          </p>

          {/* The wait, said out loud rather than hidden behind a spinner. A learner who does
              not know a batch can take an hour concludes after ten minutes that it is broken,
              and the honest answer is that the push is what brings them back. */}
          {run.state === 'deepening' && <p className="muted small">{t('res.progress.slow')}</p>}

          {/*
            What is arriving, and a line of what it says (F-147, F-149).

            The bars are off here and only here: every subject is listed below with its own bar,
            its own state and its own two remedies, and a second list of the same twelve subjects
            above it is exactly the duplication F-129 was written to end. What this adds to that
            list is the shape of the run — how many are open, how the material arrived over the
            hour, and a paragraph of what the learner is about to be taught.
          */}
          <ResearchPulse
            objectiveId={objectiveId}
            progress={progress}
            starting={false}
            bars={false}
          />

          {/*
            The owner's question, answered where he asked it (F-129): *"I have 9 out of 12
            researched and my plan is just 5 subjects."* Both numbers come from one answer, so
            they cannot disagree, and the list below says what happened to each of the ones that
            did not make it.

            When the plan predates the join there is no comparison to draw and the sentence says
            so instead of drawing a false one — that is the state the two objectives being taught
            from today are in.
          */}
          {comparable && summary.known && (
            <p className="muted small">
              {t('cov.summary')
                .replace('{researched}', String(summary.researched))
                .replace('{inPlan}', String(summary.inPlan))}
              {summary.missing > 0
                ? ` · ${t('cov.summary.missing').replace('{missing}', String(summary.missing))}`
                : ''}
            </p>
          )}
          {comparable && !summary.known && (
            <p className="muted small">{t('cov.summary.unknown')}</p>
          )}
          {comparable && summary.pending > 0 && (
            <p className="muted small">
              {t('cov.summary.pending').replace('{pending}', String(summary.pending))}
            </p>
          )}

          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}

          {/* The same "this page has stopped watching" escape every other long wait gets. */}
          {stopped && working && (
            <div className="actions">
              {/* Resumes the watcher rather than reading once. A "check now" that leaves the
                  page just as unwatched as it was is a button that has to be pressed again
                  every thirty seconds for an hour. */}
              <button type="button" className="button" onClick={() => setWatch((r) => r + 1)}>
                {t('res.progress.check')}
              </button>
            </div>
          )}

          <ul className="gaps">
            {subjects.map((subject) => {
              const verdict = verdicts.get(subject.subjectId);
              /*
               * The outcome in the learner's language, except where it would say less than the
               * state already does.
               *
               * `unfunded` and `failed` both become `barren` in the report, because what the
               * screen has to *offer* for them is the same two remedies. What they *mean* is
               * not the same — "the run stopped before reaching this" and "the request failed"
               * are facts about us, where "nothing found" is a fact about the subject — and
               * relabelling them would tell somebody the world is empty of a subject nobody
               * ever looked for.
               */
              const label =
                verdict && !KEEPS_ITS_STATE.has(subject.state)
                  ? t(verdict.outcomeKey)
                  : t(`res.state.${subject.state}` as CopyKey);
              /*
               * The reason, and it is never allowed to be nothing. The words of the pass that
               * decided when there are any — never paraphrased, they are about this learner's
               * own subject — and a sentence of ours for every row written before those fields
               * existed. "Not in your plan" with silence under it is the bug the whole report
               * exists to remove.
               */
              const why = verdict?.why || (verdict?.whyKey ? t(verdict.whyKey) : '');
              /* An empty row of buttons is still a row: a subject that was researched and is
                 in the plan has nothing to offer, and the gap it would leave under every one of
                 twelve rows is what makes the four that do have something hard to pick out. */
              const offers = !working && verdict !== undefined && verdict.remedies.length > 0;

              return (
                <li key={subject.subjectId}>
                  <p className="meta">
                    <strong>{subject.title}</strong> <span className={PILL[subject.state]}>{label}</span>
                    {/* Not an error tint, deliberately — `.pill-off` is the quiet outline the
                        plan screen uses for a subject that is switched off. A subject that did
                        not reach the plan is a thing to know and act on, not a fault report. */}
                    {verdict?.inPlan === false && (
                      <span className="pill pill-off">{t('cov.notInPlan')}</span>
                    )}
                  </p>
                  <p className="muted small">{subject.abstract}</p>

                  {/* One bar per subject, which is the level the work actually happens at: a
                      wave researches six subjects and the overall number moves once, while
                      these six move independently and say which of them is in flight. */}
                  <progress value={subjectFraction(subject)} max={1} />

                  {(subject.characters > 0 || (subject.attempts ?? 0) > 1) && (
                    <p className="muted small">
                      {subject.characters > 0
                        ? `${subject.characters.toLocaleString(locale)} ${t('res.characters')}`
                        : ''}
                      {/*
                        The second attempt, said out loud (F-147).

                        A subject that has been started twice has usually sat at *reading* for
                        twice as long as its neighbours, and the row has counted it since F-126
                        while the screen said nothing — so the one honest explanation for the
                        slowest thing on the page was on the row all along. Only ever shown
                        above one: "attempt 1" is every subject and is noise.
                      */}
                      {(subject.attempts ?? 0) > 1
                        ? `${subject.characters > 0 ? ' · ' : ''}${t('res.attempts').replace(
                            '{attempts}',
                            String(subject.attempts),
                          )}`
                        : ''}
                    </p>
                  )}

                  {/*
                    What this subject's own research says it could not find (F-147).

                    **Written on the row by the pass that looked, carried to the browser since
                    F-147, and shown here while the run is still going.** It was readable only
                    through the coverage report, which needs a plan to compare against — so the
                    most actionable sentence in the whole run, the one that says which page from
                    a book the learner owns would close the gap, appeared twenty minutes after
                    the moment they could have acted on it. It is the pass's own words and is
                    never paraphrased.

                    Compared against the verdict's sentence rather than suppressed whenever there
                    is one: the coverage report reads *this same attribute* as its reason for a
                    barren subject and synthesis's own sentence for one it declined, so the two
                    are sometimes the same string and sometimes two different facts. Equality is
                    the only test that prints both when they differ and neither twice when they
                    do not.
                  */}
                  {subject.missing && subject.missing !== why && (
                    <p className="muted small">
                      <strong>{t('res.missing')}</strong> {subject.missing}
                    </p>
                  )}

                  {verdict?.inPlan === true && (
                    <p className="muted small">
                      {verdict.nodes === 1
                        ? t('cov.inPlan.one')
                        : t('cov.inPlan.many').replace('{nodes}', String(verdict.nodes))}
                    </p>
                  )}

                  {verdict?.inPlan === false && why && <p className="muted small">{why}</p>}

                  {offers && (
                    <div className="actions-inline">
                      {/* Never `primary`. The thing to press on this screen is the fold or the
                          upload; these are what to do about a subject that came back short. */}
                      {verdict.remedies.includes('upload') && (
                        <button type="button" className="button" onClick={goToUpload}>
                          {t('cov.remedy.upload')}
                        </button>
                      )}
                      {verdict.remedies.includes('again') && DEEPENABLE.has(subject.state) && (
                        <button
                          type="button"
                          className="button"
                          disabled={deepening !== undefined}
                          onClick={() => void goDeeper(subject)}
                        >
                          {deepening === subject.subjectId
                            ? t('res.deeper.working')
                            : /* "Go deeper" is the wrong invitation for a subject nothing was
                                 found for: there is nothing to go deeper into. The second look
                                 is the same call either way; the words say which of the two
                                 situations the learner is in. */
                              verdict.outcome === 'barren' || verdict.inPlan === false
                              ? t('cov.remedy.again')
                              : t('res.deeper')}
                        </button>
                      )}
                    </div>
                  )}

                  {/*
                   * A settled subject with no verdict at all: the report could not be read, or
                   * this objective has none. The deeper pass is still offered, exactly as it was
                   * before any of this existed, because that control never depended on coverage.
                   */}
                  {!verdict && DEEPENABLE.has(subject.state) && !working && (
                    <div className="actions-inline">
                      <button
                        type="button"
                        className="button"
                        disabled={deepening !== undefined}
                        onClick={() => void goDeeper(subject)}
                      >
                        {deepening === subject.subjectId
                          ? t('res.deeper.working')
                          : t('res.deeper')}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {run.error && (
            <p className="problem" role="alert">
              {run.error}
            </p>
          )}
        </section>
      )}

      {documents && documents.length > 0 && (
        <section className="panel">
          <details className="history" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
            <summary>
              <h2>{t('res.title')}</h2>
              <span className="muted small">
                {documents.reduce((total, doc) => total + doc.characters, 0).toLocaleString(locale)}{' '}
                {t('res.characters')}
              </span>
            </summary>

            <p className="muted">{t('res.hint')}</p>

            {documents.map((doc) => (
              <article key={doc.key}>
                <p className="meta small">
                  {doc.title} · {when.format(new Date(doc.producedAt))} · {doc.origin} ·{' '}
                  {doc.characters.toLocaleString(locale)} {t('res.characters')}
                </p>
                {/* Deliberately raw, in a scrolling block, rather than rendered as Markdown.
                    The document's value here is that it is the exact text the curriculum was
                    built from — including the tables and the inline URLs — and a renderer that
                    swallowed a malformed table or a citation would be hiding the thing worth
                    checking. */}
                <pre className="extracted research">
                  {doc.body}
                  {doc.truncated ? `\n\n… ${t('res.truncated')}` : ''}
                </pre>
              </article>
            ))}
          </details>
        </section>
      )}
    </>
  );
}

/**
 * How far through its own life one subject is.
 *
 * **Duplicated from `SUBJECT_PROGRESS` in `@nabu/shared`, deliberately and unhappily.** The
 * shared barrel re-exports `keys.ts`, which imports `node:crypto`; a value import from it
 * would pull that into a browser bundle, and reaching past the barrel into a build artefact
 * would be worse. Every other consumer of these numbers gets the shared table — this one
 * cannot, so it gets a copy with this note on it.
 *
 * The `overall` fraction is *not* duplicated: it comes from the API, computed once, precisely
 * so the browser cannot hold a second opinion about the number that matters most.
 */
function subjectFraction(subject: ResearchSubject): number {
  switch (subject.state) {
    case 'outlined':
      return 0.25;
    case 'queued':
      return 0.4;
    case 'researching':
      return 0.6;
    default:
      return 1;
  }
}
